import {FC} from 'react';
import type {SelfEmploymentProvider} from '@rocket-ui/lk-api';
import {ComponentSwitch} from '@rocket-ui/components';
import {
	DocumentPhotosStep,
	FIND_INN_URL,
	ForeignDocumentDetailsStep,
	IdentityReviewStep,
	InnStep,
	isKazakhstanCitizenship,
	KAZAKHSTAN_DOCUMENT_TYPE_OPTIONS,
	MyTaxAccessErrorStep,
	MyTaxAccessStep,
	MyTaxCheckingStep,
	PartnerAgreementCheckingStep,
	PartnerAgreementPendingStep,
	MyTaxFnsPendingStep,
	MyTaxIntroStep,
	MyTaxLoadErrorStep,
	MyTaxNameMismatchStep,
	MyTaxNotSelfEmployedStep,
	MyTaxSuccessStep,
	PassportDetailsStep,
	PersonalInfoStep,
	ResidenceDocumentStep,
	RegistrationCompleteStep,
	RegistrationPaymentMethod,
	RegistrationStep,
	RegistrationWorkStatus,
	ResidentCodeStep,
	SnilsStep,
	TaxIdentifierStep,
	WORK_STATUS_STEP_ENABLED,
	WorkStatusStep,
	requiresPassportDepartmentCode,
	requiresPassportIdentificationNumber,
	requiresPassportLatinNames,
	requiresPassportRegistrationAddress,
	usesBelarusPassportNumber,
} from 'features/registration';
import {PaymentMethodStepSwitch, useCardBankResolvedHandler} from './PaymentMethodStepSwitch';
import type {RegistrationDerived} from './useRegistrationDerived';
import type {RegistrationPatchers} from './useRegistrationPatchers';
import type {RegistrationWizardState} from './useRegistrationWizardState';

type Props = {
	state: RegistrationWizardState;
	derived: RegistrationDerived;
	patchers: RegistrationPatchers;
	onWorkStatusSelect: (status: RegistrationWorkStatus, provider?: SelfEmploymentProvider) => void;
	onSelectFreeSmz?: () => void;
	onOpenAgreement?: (url: string, provider?: SelfEmploymentProvider) => void;
	onConnectMoreChange?: (connectMore: boolean) => void;
	onPaymentMethodSelect: (method: RegistrationPaymentMethod) => void;
	onEditIdentity: (target: RegistrationStep) => void;
	onRestartFromCitizenship: () => void;
	onRestartFromResidence: () => void;
};

/** Экран текущего шага регистрации. */
export const RegistrationStepContent: FC<Props> = ({
	state,
	derived,
	patchers,
	onWorkStatusSelect,
	onSelectFreeSmz,
	onOpenAgreement,
	onConnectMoreChange,
	onPaymentMethodSelect,
	onEditIdentity,
	onRestartFromCitizenship,
	onRestartFromResidence,
}) => {
	const handleCardBankResolved = useCardBankResolvedHandler(state.setPaymentDetails);
	const {returnToIdentityReview} = state;
	const {combinesDocumentPhotosWithDetails, usesForeignDocumentDetails} = derived;

	return (
		<ComponentSwitch value={state.step}>
			<ComponentSwitch.Case statement={RegistrationStep.IdentificationInn}>
				{state.registrationFlow.taxIdType === 'russian_inn' ? (
					<InnStep
						values={state.innStep}
						error={state.innError}
						onChange={patchers.patchInnStep}
						onFindInn={() => window.open(FIND_INN_URL, '_blank', 'noopener,noreferrer')}
					/>
				) : (
					<TaxIdentifierStep
						values={state.taxIdentifierStep}
						error={state.innError}
						onChange={patchers.patchTaxIdentifierStep}
					/>
				)}
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.PersonalInfo}>
				<PersonalInfoStep
					values={state.personalInfo}
					onChange={patchers.patchPersonalInfo}
					hideCitizenship={returnToIdentityReview}
					title={returnToIdentityReview ? 'Личные данные' : undefined}
					inn={
						returnToIdentityReview && !usesForeignDocumentDetails
							? {values: state.innStep, error: state.innError, onChange: patchers.patchInnStep}
							: undefined
					}
				/>
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.DocumentPhotos}>
				<DocumentPhotosStep
					config={state.documentsFlow.passport}
					values={state.documentsUpload.slots}
					onSelect={patchers.setDocumentPhoto}
					onRemove={patchers.removeDocumentPhoto}
					documentTypeOptions={
						isKazakhstanCitizenship(state.personalInfo.citizenshipOksmCode)
							? KAZAKHSTAN_DOCUMENT_TYPE_OPTIONS
							: undefined
					}
					documentType={state.foreignDocumentDetails.documentType}
					onDocumentTypeChange={(documentType) =>
						patchers.patchForeignDocumentDetails({documentType})
					}
					title={
						returnToIdentityReview
							? usesForeignDocumentDetails
								? 'Фото документов'
								: 'Фото паспорта'
							: undefined
					}
				/>
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.DocumentDetails}>
				{usesForeignDocumentDetails ? (
					<ForeignDocumentDetailsStep
						values={state.foreignDocumentDetails}
						onChange={patchers.patchForeignDocumentDetails}
						citizenshipOksmCode={state.personalInfo.citizenshipOksmCode}
						residenceOksmCode={state.personalInfo.residenceOksmCode}
						title={returnToIdentityReview ? 'Данные документов' : undefined}
						photoSlots={
							combinesDocumentPhotosWithDetails ? state.documentsFlow.passport.slots : undefined
						}
						photoGroupLabel={state.documentsFlow.passport.groupLabel}
						photoValues={state.documentsUpload.slots}
						onPhotoSelect={
							combinesDocumentPhotosWithDetails ? patchers.setDocumentPhoto : undefined
						}
						onPhotoRemove={
							combinesDocumentPhotosWithDetails ? patchers.removeDocumentPhoto : undefined
						}
					/>
				) : (
					<PassportDetailsStep
						values={state.passportDetails}
						onChange={patchers.patchPassportDetails}
						showRegistrationAddress={requiresPassportRegistrationAddress(
							state.personalInfo.residenceOksmCode,
							state.personalInfo.citizenshipOksmCode,
						)}
						showIdentificationNumber={requiresPassportIdentificationNumber(
							state.personalInfo.citizenshipOksmCode,
						)}
						showLatinNames={requiresPassportLatinNames(state.personalInfo.citizenshipOksmCode)}
						showDepartmentCode={requiresPassportDepartmentCode(
							state.personalInfo.citizenshipOksmCode,
						)}
						belarusPassportNumber={usesBelarusPassportNumber(
							state.personalInfo.citizenshipOksmCode,
						)}
						photoSlots={state.documentsFlow.passport.slots}
						photoGroupLabel={state.documentsFlow.passport.groupLabel}
						photoValues={state.documentsUpload.slots}
						onPhotoSelect={patchers.setDocumentPhoto}
						onPhotoRemove={patchers.removeDocumentPhoto}
						title={returnToIdentityReview ? 'Паспортные данные' : undefined}
					/>
				)}
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.ResidenceDocument}>
				{state.documentsFlow.residence ? (
					<ResidenceDocumentStep
						config={state.documentsFlow.residence}
						values={state.documentsUpload.slots.residenceDocument ?? {photo: null, previewUrl: null}}
						address={state.documentsUpload.residenceAddress}
						onAddressChange={patchers.setResidenceAddress}
						onPhotoSelect={(file) => patchers.setDocumentPhoto('residenceDocument', file)}
						onPhotoRemove={() => patchers.removeDocumentPhoto('residenceDocument')}
					/>
				) : null}
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.IdentityReview}>
				<IdentityReviewStep
					personalInfo={state.personalInfo}
					documentPreviewUrls={derived.documentPreviewUrls}
					passportDetails={state.passportDetails}
					foreignDocumentDetails={state.foreignDocumentDetails}
					useForeignDocumentDetails={usesForeignDocumentDetails}
					residencePreviewUrl={derived.residencePreviewUrl}
					residenceAddress={state.documentsUpload.residenceAddress}
					onEditPersonalInfo={() => onEditIdentity(RegistrationStep.PersonalInfo)}
					onEditDocumentDetails={() =>
						onEditIdentity(
							usesForeignDocumentDetails && !combinesDocumentPhotosWithDetails
								? RegistrationStep.DocumentPhotos
								: RegistrationStep.DocumentDetails,
						)
					}
					onEditResidenceDocument={() => onEditIdentity(RegistrationStep.ResidenceDocument)}
					onRestartFromCitizenship={onRestartFromCitizenship}
					onRestartFromResidence={onRestartFromResidence}
				/>
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.PaymentMethod}>
				<PaymentMethodStepSwitch
					workStatus={state.workStatus}
					paymentMethods={state.registrationFlow.paymentMethods}
					pendingPaymentMethod={state.pendingPaymentMethod}
					paymentSubStep={state.paymentSubStep}
					paymentDetails={state.paymentDetails}
					personalInfo={state.personalInfo}
					hasExistingPaymentWays={derived.hasExistingPaymentWays}
					onPaymentMethodSelect={onPaymentMethodSelect}
					onPatchPaymentDetails={patchers.patchPaymentDetails}
					onCardBankResolved={handleCardBankResolved}
				/>
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.WorkStatus}>
				{WORK_STATUS_STEP_ENABLED ? (
					<WorkStatusStep
						value={state.workStatus}
						onChange={onWorkStatusSelect}
						onSelectFreeSmz={onSelectFreeSmz}
						allowedStatuses={state.registrationFlow.allowedWorkStatuses}
						connectionStates={state.workStatusConnections}
						statusesReady={state.workStatusesReady}
						onOpenAgreement={onOpenAgreement}
						onConnectMoreChange={onConnectMoreChange}
					/>
				) : null}
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.Inn}>
				{state.registrationFlow.taxIdType === 'russian_inn' ? (
					<InnStep
						values={state.innStep}
						error={state.innError}
						onChange={patchers.patchInnStep}
						onFindInn={() => window.open(FIND_INN_URL, '_blank', 'noopener,noreferrer')}
					/>
				) : (
					<TaxIdentifierStep
						values={state.taxIdentifierStep}
						onChange={patchers.patchTaxIdentifierStep}
					/>
				)}
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.Snils}>
				<SnilsStep values={state.snilsStep} onChange={patchers.patchSnilsStep} />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.ResidentCode}>
				<ResidentCodeStep values={state.residentCodeStep} onChange={patchers.patchResidentCodeStep} />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxIntro}>
				<MyTaxIntroStep
					workStatus={state.workStatus}
					registrationFlow={state.registrationFlow}
					selfEmploymentProvider={state.selfEmploymentPartner}
				/>
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxAccess}>
				<MyTaxAccessStep
					workStatus={state.workStatus}
					selfEmploymentProvider={state.selfEmploymentPartner}
				/>
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxChecking}>
				<MyTaxCheckingStep mode={state.myTaxCheckingMode} workStatus={state.workStatus} />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.PartnerAgreementChecking}>
				<PartnerAgreementCheckingStep />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.PartnerAgreementPending}>
				<PartnerAgreementPendingStep />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxSuccess}>
				<MyTaxSuccessStep workStatus={state.workStatus} />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxNameMismatch}>
				<MyTaxNameMismatchStep mismatch={derived.resolvedMyTaxNameMismatch} />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxAccessError}>
				<MyTaxAccessErrorStep workStatus={state.workStatus} />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxNotSelfEmployed}>
				<MyTaxNotSelfEmployedStep />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxFnsPending}>
				<MyTaxFnsPendingStep />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.MyTaxLoadError}>
				<MyTaxLoadErrorStep workStatus={state.workStatus} />
			</ComponentSwitch.Case>
			<ComponentSwitch.Case statement={RegistrationStep.RegistrationComplete}>
				<RegistrationCompleteStep />
			</ComponentSwitch.Case>
		</ComponentSwitch>
	);
};
